import { OrganizationCodes } from '@revenuemarket/bank';

export enum CompanyUserPermission {
  /** 회사 관리 */
  CAN_MANAGE_COMPANY = 'CAN_MANAGE_COMPANY',
  /** 회사 삭제 */
  CAN_MANAGE_COMPANY_DELETION = 'CAN_MANAGE_COMPANY_DELETION',
  /** 구성원 관리 */
  CAN_MANAGE_COMPANY_USERS = 'CAN_MANAGE_COMPANY_USERS',
  /** 데이터 연동 관리 */
  CAN_MANAGE_DATA_INTEGRATION = 'CAN_MANAGE_DATA_INTEGRATION',
  /** 분류 규칙 관리 */
  CAN_MANAGE_LABELING_RULES = 'CAN_MANAGE_LABELING_RULES',
  /** 은행 거래내역 분류 */
  CAN_MANAGE_BANK_TRANSACTIONS_LABELING = 'CAN_MANAGE_BANK_TRANSACTIONS_LABELING',
  /** 카드 청구내역 분류 */
  CAN_MANAGE_CARD_BILLINGS_LABELING = 'CAN_MANAGE_CARD_BILLINGS_LABELING',
  /** 매출/매입 정산 */
  CAN_MANAGE_SALES_PURCHASES_SETTLEMENT = 'CAN_MANAGE_SALES_PURCHASES_SETTLEMENT',
  /** 계좌 */
  CAN_MANAGE_ACCOUNTS = 'CAN_MANAGE_ACCOUNTS',
  /** 거래처 관리 */
  CAN_MANAGE_BUSINESS_ENTITIES = 'CAN_MANAGE_BUSINESS_ENTITIES',
  /** 손익, 분석 조회 및 관리 */
  CAN_MANAGE_ANALYSIS = 'CAN_MANAGE_ANALYSIS',
  /** 클로브금융 관리 */
  CAN_MANAGE_CLOBE_FINANCE = 'CAN_MANAGE_CLOBE_FINANCE',
  /** 세금계산서 발급 관리 */
  CAN_MANAGE_TAX_INVOICE_ISSUANCE = 'CAN_MANAGE_TAX_INVOICE_ISSUANCE',
  /** 이체 */
  CAN_MANAGE_WIRING = 'CAN_MANAGE_WIRING',
  /** 알림 */
  CAN_MANAGE_NOTIFICATIONS = 'CAN_MANAGE_NOTIFICATIONS',
  /** 세금계산서 관리 */
  CAN_MANAGE_TAX_INVOICE = 'CAN_MANAGE_TAX_INVOICE',
  /** 현금영수증 관리 */
  CAN_MANAGE_CASH_RECEIPT = 'CAN_MANAGE_CASH_RECEIPT',
  /** 은행 거래내역 관리 */
  CAN_MANAGE_BANK_TRANSACTION = 'CAN_MANAGE_BANK_TRANSACTION',
  /** 카드 이용내역 관리 */
  CAN_MANAGE_CARD_TRANSACTION = 'CAN_MANAGE_CARD_TRANSACTION',
  /** 카드 매출내역 관리 */
  CAN_MANAGE_CARD_SALES = 'CAN_MANAGE_CARD_SALES',
  /** 카드청구내역 매핑 */
  CAN_MANAGE_CARD_BILLING_MAPPING = 'CAN_MANAGE_CARD_BILLING_MAPPING',
  /** PG/마켓플레이스 매출 관리 */
  CAN_MANAGE_SETTLEMENT_ORGANIZATION_MONTHLY_SALES_VAT = 'CAN_MANAGE_SETTLEMENT_ORGANIZATION_MONTHLY_SALES_VAT',
}

export enum AssetPermission {
  OWNER = 'OWNER',
  MANAGER = 'MANAGER',
  VIEWER = 'VIEWER',
  ISSUER = 'ISSUER',
}

export enum RoleType {
  CUSTOM = 'CUSTOM',
  SYSTEM_OWNER = 'SYSTEM_OWNER',
  SYSTEM_MANAGER = 'SYSTEM_MANAGER',
  SYSTEM_OBSERVER = 'SYSTEM_OBSERVER',
  SYSTEM_DEFAULT = 'SYSTEM_DEFAULT',
}

export interface RoleDetailMeta {
  id: number;
  companyId: string;
  name: string;
  description: string;
  active: boolean;
  isSystemRole: boolean;
  type: RoleType;
}

export interface RoleItem extends RoleDetailMeta {
  companyUserPermissions: CompanyUserPermission[];
  dataUserRelations: IDataUserRelationsItem[];
  assignedUsers: IAssignedUsersItem[];
}

export enum UserRelationObjectType {
  USER = 'USER',
  COMPANY = 'COMPANY',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  CORPORATE_CARD = 'CORPORATE_CARD',
  TAX_INVOICE = 'TAX_INVOICE',
  TAX_INVOICE_ISSUANCE = 'TAX_INVOICE_ISSUANCE',
}

export interface IRelation<T> {
  relationName: T;
  priority: number;
  objectType: UserRelationObjectType;
}

interface IDataUserRelationsItem {
  objectId: string;
  objectType: UserRelationObjectType;
  relation: IRelation<CompanyUserPermission>;
  isAllowAll: boolean;
}

interface IAssignedUsersItem {
  userId: string;
  userName: string;
}

interface IPermissionsItem {
  objectId: string;
  relation: AssetPermission;
}

interface BankPermissionItem extends IPermissionsItem {
  organizationCode: string;
  organizationName: string;
  accountType: string;
  currencyCode: string;
  accountNumber: string;
  accountName: string;
  accountAlias: string;
}

interface CorporateCardPermissionItem extends IPermissionsItem {
  cardNumber: string;
  userNames: string[];
  organizationCode: OrganizationCodes;
  organizationName: string;
}

interface TaxInvoicePermissionItem extends IPermissionsItem {}
interface TaxInvoiceIssuancePermissionItem extends IPermissionsItem {}
interface CashReceiptPermissionItem extends IPermissionsItem {}

interface DataPermissions<T> {
  permissions: T[];
  allowAllView: boolean;
  allowAllEdit: boolean;
}

export interface BankAccountPermissions extends DataPermissions<BankPermissionItem> {}
export interface CorporateCardPermissions extends DataPermissions<CorporateCardPermissionItem> {}
export interface TaxInvoicePermissions extends DataPermissions<TaxInvoicePermissionItem> {}
export interface CashReceiptPermissions extends DataPermissions<CashReceiptPermissionItem> {}
export interface SecurityAccountPermissions extends DataPermissions<SecurityAccountPermissionInfo> {}

export interface TaxInvoiceIssuancePermissions extends DataPermissions<TaxInvoiceIssuancePermissionItem> {}
interface IAssignedUsersWithPermissionsItem {
  userId: string;
  userName: string;
}

export interface RoleDetailResponse extends RoleDetailMeta {
  companyUserPermissions: CompanyUserPermission[];
  bankAccountPermissions: BankAccountPermissions;
  corporateCardPermissions: CorporateCardPermissions;
  taxInvoicePermissions: TaxInvoicePermissions;
  cashReceiptPermissions: CashReceiptPermissions;
  securityAccountPermissions: SecurityAccountPermissionCollection;
  assignedUsers: IAssignedUsersWithPermissionsItem[];
}

export interface RoleListResponse {
  items: RoleItem[];
}

export type AvailableBankAccountPermissionItem = Omit<BankPermissionItem, 'relation'> & {
  relationName: AssetPermission;
};

export type AvailableCorporateCardPermissionItem = Omit<CorporateCardPermissionItem, 'relation'> & {
  relationName: AssetPermission;
};

export interface assignableUser {
  userId: string;
  userName: string;
  userEmail: string;
  position: string;
  roles: IRolesItem[];
}
interface IRolesItem {
  roleId: number;
  roleName: string;
  roleType: string;
  roleDescription: string;
}

export interface ICompanyPermissionItem {
  relationName: CompanyUserPermission;
  isPublic: boolean;
  description: string;
}

export interface SecurityAccountPermissionInfo {
  objectId: string;
  organizationCode: string;
  organizationName: string;
  accountNumber: string;
  accountName: string;
  accountAlias: string | null;
  relation: AssetPermission;
}

export interface SecurityAccountPermissionCollection {
  permissions: SecurityAccountPermissionInfo[];
  allowAllView: boolean;
  allowAllEdit: boolean;
}

export interface CompanyUserInfo {
  userId: string;
  userName: string;
  userEmail: string;
  position: string;
  roles: IRolesItem[];
}

export interface AvailablePermissionsResponse {
  assignableUsers: CompanyUserInfo[];
  companyPermissions: ICompanyPermissionItem[];
  bankAccountPermissions: AvailableBankAccountPermissionItem[];
  corporateCardPermissions: AvailableCorporateCardPermissionItem[];
  taxInvoicePermissions: { relationName: AssetPermission }[];
  taxInvoiceIssuancePermissions: { relationName: AssetPermission }[];
  securityAccountPermissions: (SecurityAccountPermissionInfo & { relationName: AssetPermission })[];
}

export interface CreateRoleResponse {
  id: number;
  companyId: string;
  name: string;
  description: string;
  active: boolean;
  companyUserRelations: string[];
  dataUserRelations: IDataUserRelationsItem[];
  assignedUsers: IAssignedUsersItem[];
}

export type UpdateRolePermission = DataPermissions<{
  objectId: string;
  relationName: AssetPermission;
}>;

export interface UpdateRoleData {
  name: string;
  description: string;
  active: boolean;
  companyUserPermissions: CompanyUserPermission[];
  bankAccountPermissions: UpdateRolePermission;
  corporateCardPermissions: UpdateRolePermission;
  taxInvoicePermissions: UpdateRolePermission;
  cashReceiptPermissions: UpdateRolePermission;
  securityAccountPermissions: UpdateRolePermission;
  assignedUsers: {
    userId: string;
  }[];
}
